/**
 * Choosing a new password from the emailed link (/reset-password?oobCode=…).
 *
 * The reset and welcome emails link here instead of Firebase's default action
 * page, so the password is chosen on the same domain as the login page and a
 * password the browser saves fills in there (molnfast-react#1783).
 */
import {
    browserLocalPersistence,
    confirmPasswordReset,
    signInWithEmailAndPassword,
    signOut,
    verifyPasswordResetCode,
} from 'firebase/auth';
import { get, ref } from 'firebase/database';
import { auth, db } from '../firebase';

export const MIN_PASSWORD_LENGTH = 6;

/** Codes Firebase returns for a link that has expired, was used or is malformed */
const BAD_LINK_CODES = new Set([
    'auth/expired-action-code',
    'auth/invalid-action-code',
    'auth/user-disabled',
    'auth/user-not-found',
]);

/** The link can no longer be used; the page offers to order a new one */
export class BadResetLinkError extends Error {
    constructor() {
        super('Länken har slutat gälla eller har redan använts.');
        this.name = 'BadResetLinkError';
    }
}

const errorCode = (err: unknown): string | undefined =>
    typeof err === 'object' && err !== null && 'code' in err && typeof err.code === 'string'
        ? err.code
        : undefined;

/** The email the link was sent to; throws BadResetLinkError when it can no longer be used */
export const verifyResetLink = async (oobCode: string): Promise<string> => {
    try {
        return await verifyPasswordResetCode(auth, oobCode);
    } catch (err) {
        if (BAD_LINK_CODES.has(errorCode(err) ?? '')) throw new BadResetLinkError();
        throw new Error('Det gick inte att läsa länken. Kontrollera anslutningen och försök igen.');
    }
};

/** Where the user ends up once the password is saved */
export type ResetOutcome =
    /** Signed in with web access: go to the start page */
    | 'signedIn'
    /** No web access: they use the iOS app */
    | 'appOnly'
    /** The password is saved but signing in failed (e.g. two-step login): log in by hand */
    | 'signInFailed';

/**
 * Save the new password, then sign in with it on this domain so the browser
 * offers to save it here. Anyone else signed in in this browser is signed out
 * first. Users without web access are signed out again, like on the login page.
 */
export const saveNewPassword = async (
    oobCode: string,
    email: string,
    newPassword: string
): Promise<ResetOutcome> => {
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
        throw new Error(`Lösenordet måste vara minst ${MIN_PASSWORD_LENGTH} tecken långt.`);
    }

    try {
        await confirmPasswordReset(auth, oobCode, newPassword);
    } catch (err) {
        const code = errorCode(err);
        if (code === 'auth/weak-password') {
            throw new Error('Lösenordet är för svagt. Välj ett längre lösenord.');
        }
        if (BAD_LINK_CODES.has(code ?? '')) throw new BadResetLinkError();
        throw new Error('Det gick inte att spara lösenordet. Försök igen.');
    }

    try {
        if (auth.currentUser) await signOut(auth);
        await auth.setPersistence(browserLocalPersistence);
        const { user } = await signInWithEmailAndPassword(auth, email, newPassword);
        const accessSnap = await get(ref(db, `users/userList/${user.uid}/accessToWeb`));
        if (accessSnap.val() === true) return 'signedIn';
        await signOut(auth);
        return 'appOnly';
    } catch (err) {
        console.error('Sign-in after password reset failed:', err);
        if (auth.currentUser) await signOut(auth).catch(() => undefined);
        return 'signInFailed';
    }
};
